import React, { useState, useEffect } from 'react';

import {
  FlowProgressHeader,
  StepMatch,
} from 'views/components/FlowProgressHeader/FlowProgressHeader';
import { FormattedMessage, IntlShape } from 'react-intl';
import { FlowButtonBar } from 'views/components/FlowButtonBar/FlowButtonBar';
import { Button } from 'views/components/Button/Button';
import { Pricing } from 'views/components/Pricing/PricingController';
import { CheckboxInput } from 'views/components/CheckboxInput/CheckboxInput';
import {
  IContact, IContactDetails, ICompanyDetails, AccountantTuple,
} from 'types';
import { scrollToError } from 'tools/utilities/scrollToError';
import { getErrorMessageFor } from 'tools/utilities/getErrorMessageFor';
import { TextInput } from 'views/components/TextInput/TextInput';
import { isValidPhone } from 'tools/utilities/validators/phone';
import { AutoComplete } from 'views/components/AutoComplete/AutoComplete';
import { ContactForm } from './ContactForm/ContactForm';

interface Props {
  intl: IntlShape;
  goToNextStep: (data: IContactDetails, accountantData: ICompanyDetails) => void;
  goToPreviousStep: () => void;
  defaultValues?: IContactDetails;
  jobTitles: string[];
  accountantDetails?: ICompanyDetails;
  accountantResults: AccountantTuple[];
  accountantLookup: (val: string) => void;
  accountantIsLoading: boolean;
  accountantError?: string;
}

export const ContactDetailsView = (props: Props) => {
  const {
    intl,
    goToNextStep,
    goToPreviousStep,
    defaultValues,
    jobTitles,
    accountantDetails,
    accountantLookup,
    accountantResults,
    accountantIsLoading,
    accountantError,
  } = props;

  const [touchedNext, setTouchedNext] = useState(false);
  const [isAccountant, setIsAccountant] = useState(defaultValues?.isAccountant || false);
  const [hasAdditionalContact, setHasAdditionalContact] = useState(!!defaultValues?.additionalContact);
  const [isAdditionalPrimaryContact, setIsAdditionalPrimaryContact] = useState(
    defaultValues?.isAdditionalPrimaryContact || false,
  );

  const [accountant, setAccountant] = useState<AccountantTuple | undefined>(accountantDetails?.accountant);
  const [accountantNotListed, setAccountantNotListed] = useState(accountantDetails?.accountantNotListed || false);
  const [accountantBusinessName, setAccountantBusinessName] = useState(accountantDetails?.accountantBusinessName || '');
  const [accountantPhoneNumber, setAccountantPhoneNumber] = useState(accountantDetails?.accountantPhoneNumber || '');
  const [accountantName, setAccountantName] = useState(accountantDetails?.accountantName || '');
  const accountantIsValid = !isAccountant || !!accountant || accountantNotListed;
  const accountantBusinessNameIsValid = !accountantNotListed || !!accountantBusinessName;
  const accountantPhoneNumberIsValid = !accountantNotListed || isValidPhone(accountantPhoneNumber);
  const accountantNameIsValid = !!accountantName;

  const isAccountantSectionValid = !isAccountant || (accountantIsValid && accountantBusinessNameIsValid && accountantPhoneNumberIsValid && accountantNameIsValid);

  const [contact, setContact] = useState<IContact | undefined>(defaultValues?.contact);
  const [contactIsValid, setContactIsValid] = useState(false);

  const [additionalContact, setAdditionalContact] = useState<IContact | undefined>(defaultValues?.additionalContact);
  const [additionalContactIsValid, setAdditionalContactIsValid] = useState(false);

  useEffect(() => {
    if (hasAdditionalContact === false) {
      setAdditionalContact(undefined);
    }
  }, [hasAdditionalContact, setAdditionalContact]);

  const isValid = contactIsValid && isAccountantSectionValid && ((hasAdditionalContact === false) || (hasAdditionalContact && additionalContactIsValid));

  const onNext = () => {
    setTouchedNext(true);

    if (!isValid) {
      scrollToError();
      return;
    }

    goToNextStep({
      contact,
      additionalContact,
      isAccountant,
      isAdditionalPrimaryContact,
    }, {
      accountant,
      accountantNotListed,
      accountantBusinessName,
      accountantPhoneNumber,
      accountantName,
    });
  };

  return (
    <section className="ContactDetails">
      <FlowProgressHeader flowMatch={StepMatch.ContactDetails} />

      <div className="Container">

        <div className="Grid">
          {/* Left */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width7of12 u-xl-width2of3">
            <div className="Tile">
              <h2>
                <FormattedMessage id="CONTACT_DETAILS" />
              </h2>

              <div className="ContactDetails-accountant">
                {/* Is accountant check */}
                <CheckboxInput
                  id="isAccountant"
                  label={intl.formatMessage({ id: 'I_AM_ACCOUNTANT' })}
                  isChecked={isAccountant}
                  value="isAccountant"
                  onChange={() => {
                    setIsAccountant(!isAccountant);
                    // Reset any accountant fields
                    setAccountantName('');
                    setAccountant(undefined);
                    setAccountantBusinessName('');
                    setAccountantPhoneNumber('');
                  }}
                />
              </div>

              {isAccountant && (
                <>
                  <div className="ContactDetails-isAccountant">
                    <h3 className="CompanyDetails-title">
                      <FormattedMessage id="ACCOUNTANT_DETAILS" />
                    </h3>

                    <TextInput
                      className="TextInput--3of4"
                      id="accountantName"
                      value={accountantName}
                      onChange={setAccountantName}
                      type="text"
                      label={intl.formatMessage({ id: 'ACCOUNTANT_NAME_FIRST_PERSON' })}
                      error={touchedNext && getErrorMessageFor(accountantNameIsValid, 'ERROR_ACCOUNTANT_NAME_FIRST_PERSON', intl)}
                      placeholder={intl.formatMessage({ id: 'ENTER_ACCOUNTANT_NAME_FIRST_PERSON' })}
                    />

                    <AutoComplete
                      id="accountant"
                      selectedItem={accountant?.name || ''}
                      onSelect={(val) => {
                        const selectedAccountantTuple = accountantResults.find(({ name }) => val === name);
                        setAccountant(selectedAccountantTuple);
                      }}
                      label={intl.formatMessage({ id: 'SELECT_YOUR_ACCOUNTANT_FIRST_PERSON' })}
                      error={touchedNext && ((!accountantIsValid && accountantError) || getErrorMessageFor(accountantIsValid, 'ERROR_SELECT_YOUR_ACCOUNTANT_FIRST_PERSON', intl))}
                      placeholder={intl.formatMessage({ id: 'START_TYPING_ACCOUNTANT_FIRST_PERSON' })}
                      clearLabel={intl.formatMessage({ id: 'BUTTON_CLEAR' })}
                      noResultsMessage={(
                        <div className="AddressFinder-noResults">
                          <FormattedMessage tagName="p" id="NO_RESULTS" />
                        </div>
                      )}
                      isLoading={accountantIsLoading}
                      items={accountantResults.map(({ name }) => name)}
                      onInputValueChange={(val) => {
                        if (val && val.length >= 3) {
                          accountantLookup(val);
                        }
                      }}
                    />

                    <Button
                      label={intl.formatMessage({ id: accountantNotListed ? 'ACCOUNTANT_HIDE_MANUAL_FIRST_PERSON' : 'ACCOUNTANT_NOT_LISTED_FIRST_PERSON' })}
                      className="CompanyDetails-accountant Button Button-link"
                      buttonType="button"
                      onClick={() => {
                        setAccountantNotListed(!accountantNotListed);

                        if (accountantNotListed) {
                          setAccountantBusinessName('');
                          setAccountantPhoneNumber('');
                        } else {
                          setAccountant(undefined);
                        }
                      }}
                    />

                    {/* Only appears if customer selects accountantNotListed */}
                    {accountantNotListed && (
                      <div>
                        <TextInput
                          id="accountantBusinessName"
                          value={accountantBusinessName}
                          onChange={setAccountantBusinessName}
                          type="text"
                          label={intl.formatMessage({ id: 'SELECT_YOUR_ACCOUNTANT_FIRST_PERSON' })}
                          error={touchedNext && getErrorMessageFor(accountantBusinessNameIsValid, 'ERROR_ACCOUNTANT_BUSINESS_NAME_FIRST_PERSON', intl)}
                          placeholder={intl.formatMessage({ id: 'ENTER_ACCOUNTANT_BUSINESS_NAME_FIRST_PERSON' })}
                        />
                        <TextInput
                          id="accountantPhoneNumber"
                          value={accountantPhoneNumber}
                          onChange={setAccountantPhoneNumber}
                          type="tel"
                          label={intl.formatMessage({ id: 'ACCOUNTANT_PHONE_NUMBER_FIRST_PERSON' })}
                          error={touchedNext && getErrorMessageFor(accountantPhoneNumberIsValid, 'ERROR_ACCOUNTANT_PHONE_NUMBER_FIRST_PERSON', intl)}
                          placeholder={intl.formatMessage({ id: 'ENTER_ACCOUNTANT_PHONE_NUMBER_FIRST_PERSON' })}
                        />
                      </div>
                    )}
                  </div>

                  <div className="ContactDetails-isAccountant">
                    <h3 className="CompanyDetails-title">
                      <FormattedMessage id="CLIENT_DETAILS" />           
                    </h3>
                    <p>
                      <FormattedMessage id="PRIMARY_CONTACT_PERSON" />
                    </p>
                  </div>
                </>
              )}

              {/* Primary contact */}
              <ContactForm
                isAdditionalContact={false}
                onChange={setContact}
                onValid={setContactIsValid}
                touchedNext={touchedNext}
                defaultContact={contact}
                jobTitles={jobTitles}
              />

              {/* OPTIONAL add another contact */}
              {!hasAdditionalContact && (
                <Button
                  icon="ic-add"
                  label={intl.formatMessage({ id: 'ADD_ANOTHER_CONTACT' })}
                  onClick={() => setHasAdditionalContact(true)}
                  className="Button"
                />
              )}

              {hasAdditionalContact && (
                <div className="ContactDetails-additional">
                  <div className="ContactDetails-additional-header">
                    <h2>
                      <FormattedMessage id="ADDITIONAL_CONTACT" />
                    </h2>
                    
                    <Button
                      icon="ic-close"
                      label={intl.formatMessage({ id: 'REMOVE_CONTACT' })}
                      onClick={() => setHasAdditionalContact(false)}
                      className="Button"
                    />
                  </div>
                  
                  <div className="ContactDetails-additional-helper">
                    <p>
                      <FormattedMessage id="ADDITIONAL_ACCOUNT_CONTACT" />
                    </p>
                  </div>

                  <ContactForm
                    isAdditionalContact
                    onChange={setAdditionalContact}
                    onValid={setAdditionalContactIsValid}
                    touchedNext={touchedNext}
                    defaultContact={additionalContact}
                    jobTitles={jobTitles}
                  />

                  <div className="ContactDetails-primaryContact">
                    {/* Make this person the primary contact checkbox */}
                    <CheckboxInput
                      id="isAdditionalPrimaryContact"
                      value="isAdditionalPrimaryContact"
                      label={intl.formatMessage({ id: 'PRIMARY_CONTACT_CHECKBOX' })}
                      isChecked={isAdditionalPrimaryContact}
                      onChange={() => {
                        setIsAdditionalPrimaryContact(
                          !isAdditionalPrimaryContact,
                        );
                      }}
                    />
                  </div>
                </div>
              )}

              <FlowButtonBar
                secondaryLabel={intl.formatMessage({ id: 'BUTTON_BACK' })}
                onPrimary={onNext}
                onSecondary={goToPreviousStep}
              />
            </div>
          </div>

          {/* Right */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width5of12 u-xl-width1of3">
            <Pricing />
          </div>

        </div>
      </div>
    </section>
  );
};
